import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Check, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import api from '../../lib/axios';
import { useAuth } from '../../context/AuthContext';

const STEPS = ['Business', 'Branding', 'Hours', 'Ready'];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Paris', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore',
  'Australia/Sydney',
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'AED', 'SGD'];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEFAULT_HOURS = DAYS.map((_, i) => ({
  enabled:    i >= 1 && i <= 6, // Mon–Sat default
  start_time: '10:00',
  end_time:   '20:00',
}));

// ── Step schemas ──────────────────────────────────────────────────────────────
const step1Schema = z.object({
  name:     z.string().min(2, 'Required'),
  phone:    z.string().optional(),
  timezone: z.string().min(1, 'Required'),
  currency: z.string().min(1, 'Required'),
  booking_lead_time_minutes:  z.coerce.number().min(0).max(1440),
  cancellation_window_hours:  z.coerce.number().min(0).max(168),
});

const step2Schema = z.object({
  primary_color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be a valid hex color'),
});

// ── Progress bar ──────────────────────────────────────────────────────────────
function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all
              ${i < current ? 'bg-primary text-white' : i === current ? 'bg-primary text-white ring-4 ring-primary/20' : 'bg-surface-200 text-ink-muted'}`}>
              {i < current ? <Check size={14} /> : i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${i === current ? 'text-ink' : 'text-ink-muted'}`}>{label}</span>
          </div>
          {i < total - 1 && (
            <div className={`flex-1 h-0.5 rounded ${i < current ? 'bg-primary' : 'bg-surface-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Step 1 — Business info ────────────────────────────────────────────────────
function Step1({ onNext, tenant }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      name:     tenant?.name || '',
      phone:    tenant?.phone || '',
      timezone: tenant?.timezone || 'America/New_York',
      currency: tenant?.currency || 'USD',
      booking_lead_time_minutes: 60,
      cancellation_window_hours: 24,
    },
  });

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="label">Business name</label>
          <input {...register('name')} className={`input ${errors.name ? 'border-red-400' : ''}`} />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
        </div>

        <div className="col-span-2 sm:col-span-1">
          <label className="label">Phone (optional)</label>
          <input {...register('phone')} type="tel" placeholder="+1 555 0100" className="input" />
        </div>

        <div className="col-span-2 sm:col-span-1">
          <label className="label">Timezone</label>
          <select {...register('timezone')} className="input">
            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
          {errors.timezone && <p className="mt-1 text-xs text-red-500">{errors.timezone.message}</p>}
        </div>

        <div>
          <label className="label">Currency</label>
          <select {...register('currency')} className="input">
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label className="label">Booking lead time (min)</label>
          <input {...register('booking_lead_time_minutes')} type="number" min="0" className="input" />
        </div>

        <div className="col-span-2 sm:col-span-1">
          <label className="label">Cancellation window (hours)</label>
          <input {...register('cancellation_window_hours')} type="number" min="0" className="input" />
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button type="submit" className="btn-primary">
          Continue <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
}

// ── Step 2 — Branding ─────────────────────────────────────────────────────────
function Step2({ onNext, onBack, data }) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: zodResolver(step2Schema),
    defaultValues: { primary_color: data?.primary_color || '#2E5BBA' },
  });
  const color = watch('primary_color');

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-6">
      <div>
        <label className="label">Brand color</label>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg border border-surface-border" style={{ background: color }} />
          <input {...register('primary_color')} type="color" className="w-10 h-10 rounded cursor-pointer border-0 p-0" />
          <input {...register('primary_color')} placeholder="#2E5BBA" className={`input max-w-[140px] font-mono ${errors.primary_color ? 'border-red-400' : ''}`} />
        </div>
        {errors.primary_color && <p className="mt-1 text-xs text-red-500">{errors.primary_color.message}</p>}
        <p className="mt-2 text-xs text-ink-muted">This color appears on your booking widget and emails.</p>
      </div>

      {/* Preview */}
      <div className="border border-surface-border rounded-xl p-5 bg-surface-50">
        <p className="text-xs text-ink-muted mb-3 uppercase tracking-wide font-semibold">Widget preview</p>
        <div className="bg-white rounded-lg border border-surface-border p-4 max-w-xs">
          <div className="w-full py-2 rounded-md text-white text-sm font-medium text-center" style={{ background: color }}>
            Book Appointment
          </div>
          <div className="mt-3 space-y-1.5">
            {['Eyebrow Threading — 15 min', 'Full Face Threading — 45 min'].map(s => (
              <div key={s} className="flex items-center justify-between text-xs p-2 rounded border border-surface-border">
                <span className="text-ink">{s}</span>
                <div className="w-3 h-3 rounded-full" style={{ background: color }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} className="btn-secondary">
          <ArrowLeft size={16} /> Back
        </button>
        <button type="submit" className="btn-primary">
          Continue <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
}

// ── Step 3 — Working hours ────────────────────────────────────────────────────
function Step3({ onNext, onBack }) {
  const [hours, setHours] = useState(DEFAULT_HOURS);

  function toggle(i) {
    setHours(h => h.map((d, idx) => idx === i ? { ...d, enabled: !d.enabled } : d));
  }
  function setTime(i, field, val) {
    setHours(h => h.map((d, idx) => idx === i ? { ...d, [field]: val } : d));
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-secondary">
        Set default working hours for your staff. You can adjust per-staff hours later.
      </p>

      <div className="space-y-2">
        {DAYS.map((day, i) => (
          <div key={day} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors
            ${hours[i].enabled ? 'border-primary/30 bg-primary/5' : 'border-surface-border bg-surface-50'}`}>
            <button
              type="button"
              onClick={() => toggle(i)}
              className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors shrink-0
                ${hours[i].enabled ? 'bg-primary border-primary' : 'border-surface-200 bg-white'}`}
            >
              {hours[i].enabled && <Check size={12} className="text-white" />}
            </button>
            <span className="text-sm font-medium text-ink w-8">{day}</span>
            {hours[i].enabled ? (
              <div className="flex items-center gap-2 ml-auto">
                <input
                  type="time"
                  value={hours[i].start_time}
                  onChange={e => setTime(i, 'start_time', e.target.value)}
                  className="input w-32 text-sm"
                />
                <span className="text-ink-muted text-sm">–</span>
                <input
                  type="time"
                  value={hours[i].end_time}
                  onChange={e => setTime(i, 'end_time', e.target.value)}
                  className="input w-32 text-sm"
                />
              </div>
            ) : (
              <span className="ml-auto text-xs text-ink-muted">Closed</span>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack} className="btn-secondary">
          <ArrowLeft size={16} /> Back
        </button>
        <button type="button" onClick={() => onNext({ working_hours: hours })} className="btn-primary">
          Continue <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Step 4 — Done ─────────────────────────────────────────────────────────────
function Step4({ onFinish, loading }) {
  return (
    <div className="text-center py-6">
      <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Sparkles size={32} className="text-primary" />
      </div>
      <h3 className="text-xl font-semibold text-ink mb-2">You're all set!</h3>
      <p className="text-ink-secondary text-sm mb-8 max-w-xs mx-auto">
        Your booking platform is ready. Add services and staff to start taking appointments.
      </p>
      <div className="space-y-2 text-left max-w-xs mx-auto mb-8">
        {[
          'Add your services and pricing',
          'Set up your staff members',
          'Copy your embed snippet',
          'Paste it on your website',
        ].map((s, i) => (
          <div key={s} className="flex items-center gap-3 text-sm text-ink">
            <div className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
              {i + 1}
            </div>
            {s}
          </div>
        ))}
      </div>
      <button onClick={onFinish} disabled={loading} className="btn-primary px-8 py-2.5">
        {loading ? 'Setting up…' : 'Go to dashboard'}
      </button>
    </div>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────
export default function Onboarding() {
  const { tenant, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [step, setStep]       = useState(0);
  const [formData, setFormData] = useState({});
  const [saving, setSaving]   = useState(false);

  function next(data) {
    const merged = { ...formData, ...data };
    setFormData(merged);
    if (step < STEPS.length - 1) setStep(s => s + 1);
  }

  async function finish() {
    setSaving(true);
    try {
      // Save tenant settings
      await api.put('/api/tenant', {
        name:                       formData.name,
        phone:                      formData.phone,
        timezone:                   formData.timezone,
        currency:                   formData.currency,
        primary_color:              formData.primary_color,
        booking_lead_time_minutes:  formData.booking_lead_time_minutes,
        cancellation_window_hours:  formData.cancellation_window_hours,
      });
      await refreshMe();
      navigate('/dashboard');
    } catch {
      // Route not wired yet in Phase 2 — navigate anyway
      navigate('/dashboard');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        <div className="mb-8 text-center">
          <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center mx-auto mb-3">
            <span className="text-white font-bold">B</span>
          </div>
          <h1 className="text-2xl font-semibold text-ink">Set up your account</h1>
          <p className="text-ink-secondary text-sm mt-1">Takes about 2 minutes</p>
        </div>

        <div className="card p-8">
          <StepIndicator current={step} total={STEPS.length} />

          {step === 0 && <Step1 onNext={next} tenant={tenant} />}
          {step === 1 && <Step2 onNext={next} onBack={() => setStep(0)} data={formData} />}
          {step === 2 && <Step3 onNext={next} onBack={() => setStep(1)} />}
          {step === 3 && <Step4 onFinish={finish} loading={saving} />}
        </div>
      </div>
    </div>
  );
}
